const FocusManager = {
    trappedFocusEvent: null,
    $focusableEls: [],
    focusIndex: 0,

    /**
     * Constrains focus inside elements
     * 
     * @param {HTMLElement|HTMLElement[]} $elements Elements to constrain focus
     * 
     * @return {FocusManager}
     */
    trapFocus($elements: HTMLElement|HTMLElement[]) : typeof FocusManager {
        this.untrapFocus();

        $elements = $elements instanceof HTMLElement ? [$elements] : $elements;

        $elements.forEach($element => {
            this.$focusableEls = this.$focusableEls.concat(Array.from($element.querySelectorAll<HTMLElement>('a, button, [tabindex]')));
        });

        this.focusIndex = this.$focusableEls.indexOf(document.activeElement);

        this.trappedFocusEvent = (evt) : void => {
            if (evt.key !== 'Tab') {
                return;
            }
        
            if (evt.shiftKey) { // shift + tab
                evt.preventDefault();

                this.focusIndex--;

                if (this.focusIndex < 0) {
                    this.focusIndex = this.$focusableEls.length - 1;
                }

                while (!this.isFocusable($elements, this.$focusableEls[this.focusIndex])) {
                    this.focusIndex--;

                    if (this.focusIndex < 0) {
                        this.focusIndex = this.$focusableEls.length - 1;
                    }
                }
            } else { // tab
                evt.preventDefault();

                this.focusIndex++;

                if (this.focusIndex > this.$focusableEls.length - 1) {
                    this.focusIndex = 0;
                }

                while (!this.isFocusable($elements, this.$focusableEls[this.focusIndex])) {
                    this.focusIndex++;
    
                    if (this.focusIndex > this.$focusableEls.length - 1) {
                        this.focusIndex = 0;
                    }
                }
            }

            this.$focusableEls[this.focusIndex].focus();
        };

        document.addEventListener('keydown', this.trappedFocusEvent);

        return this;
    },

    /**
     * Check if an element is able to be focusable
     * 
     * @param {HTMLElement[]} $containers Elements to which to ascend
     * @param {HTMLElement} $element Element to check
     * 
     * @return {boolean}
     */
    isFocusable($containers: HTMLElement[], $element: HTMLElement) : boolean {
        let isFocusable = true;
        let $parent = $element;

        // If $element is out of viewport (left or right)
        const bound = $element.getBoundingClientRect();
        if (bound.left >= window.innerWidth || bound.right <= 0) {
            return false;
        }
        
        // Checks that all parent elements are visible
        while ($parent !== null && isFocusable && $containers.includes($parent)) {
            const computedStyle = window.getComputedStyle($parent);

            if (computedStyle.display === 'none' || computedStyle.opacity === '0' || computedStyle.visibility === 'hidden') {
                isFocusable = false;
            }

            $parent = $parent.parentElement;
        }

        return isFocusable;
    },

    /**
     * Remove focus constraint if there is one
     * 
     * @return {FocusManager}
     */
    untrapFocus() : typeof FocusManager {
        if (this.trappedFocusEvent) {
            document.removeEventListener('keydown', this.trappedFocusEvent);
            this.trappedFocusEvent = null;
        }

        this.$focusableEls = [];
        this.focusIndex = 0;

        return this;
    },
};

export default FocusManager;
